/**
 * Job number (E4b Item 1) — the ONE client-side implementation. Mirror of
 * backend/src/lib/jobNumber.ts; keep the two in lock-step.
 *
 * Before this phase the identifier was DERIVED from the lead's UUID
 * (`id.replace(/-/g,'').slice(0,6)` → "555fca") and that expression was copied
 * into nine files. It is now a real sequential integer, `leads.job_no`, generated
 * by a Postgres sequence (migration 020) — so uniqueness is a database guarantee,
 * not an application convention.
 *
 * PRECEDENCE (deliberate):
 *   1. job_number (text)  — the manual OVERRIDE escape hatch kept from migration
 *                           004 for the historical import. If set, it wins.
 *   2. job_no (integer)   — the sequence value. The normal case.
 *
 * The pre-migration UUID-slug fallback is GONE: 020 is applied in both
 * environments and job_no is NOT NULL, so a lead always has a number. '' is now
 * returned only when the LEAD ITSELF is missing (a row whose lead hasn't loaded)
 * — formatJobNo renders that as an em-dash rather than a bare '#'.
 */

/** The lead's job number. '' only when the lead could not be resolved at all. */
export function jobNo(lead: any): string {
  const override = lead?.job_number;
  if (override != null && String(override).trim() !== '') return String(override).trim();
  const seq = lead?.job_no;
  return seq != null && String(seq).trim() !== '' ? String(seq).trim() : '';
}

/** Display form — '#4042', or an em-dash when the lead couldn't be resolved. */
export function formatJobNo(lead: any): string {
  const n = jobNo(lead);
  return n ? `#${n}` : '—';
}

/**
 * Display form for a stored document number. Document numbers are stored BARE
 * ("4042", "4042-2") and rendered with the '#' — so the stored value stays a
 * clean sortable token and the '#' lives in exactly one place.
 */
export function formatDocNo(number: string | null | undefined): string {
  const n = String(number ?? '').trim();
  return n ? `#${n}` : '—';
}
